/*
Origin Core 1.0

called from /snippets/cart-products.liquid

DEVELOPER NOTE
--------------------
any changes to this file should be noted here, and also noted in the following wiki page:
https://unifiedco.atlassian.net/wiki/spaces/ORIGIN/pages/1413414932/Core+File+Changes+1.0

SUMMARY
--------------------
This script defines a custom web component <cart-items-component> that manages the display and interaction of cart items in Shopify. 
It handles quantity changes, item removal, and updates the cart via AJAX. 
The component listens for cart, discount, and quantity update events, morphs the cart section with new HTML, and manages error states for invalid actions. 
It also provides UI feedback such as shimmer animations and disables/enables cart items during updates. 
The component is registered if not already defined.

*/

import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSectionElement, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';

function queueMiniCartNostoRefresh() {
  window.requestAnimationFrame(() => {
    const miniCartPlacementRoot = document.querySelector('[data-cart-nosto-complete-the-look]');
    const miniCartPlacement = miniCartPlacementRoot?.querySelector('.nosto_element');

    if (!(miniCartPlacementRoot instanceof HTMLElement) || !(miniCartPlacement instanceof HTMLElement)) return;
    if (typeof window.nostojs !== 'function') return;

    window.nostojs((api) => {
      if (typeof api?.loadRecommendations === 'function') {
        api.loadRecommendations();
      }
    });
  });
}

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total (incl. VAT).
 * @property {TextComponent} cartTotalExclVat - The cart total (excl. VAT).
 * @property {TextComponent} itemCount - The cart page item count label.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    
    // Listen for cart drawer opening to refresh contents
    document.addEventListener('cart-drawer:opened', this.#handleCartDrawerOpened);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener('cart-drawer:opened', this.#handleCartDrawerOpened);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    const row = this.#getCartItemRow(line);
    const groupId = row?.dataset.personalisationGroup;
    const isGroupParent = row?.dataset.groupParent === 'true';

    if (quantity === 0 && groupId && isGroupParent) {
      return this.#removePersonalisationGroup(groupId, line);
    }

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    if (groupId && isGroupParent) {
      this.#updatePersonalisationGroup(groupId, quantity);

      const textComponent = /** @type {TextComponent | undefined} */ (row?.querySelector('text-component'));
      textComponent?.shimmer();
      return;
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = row;

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line, event) {
    const removeButton = event?.target instanceof HTMLElement ? event.target.closest('button') : null;
    const groupId = removeButton?.dataset.personalisationGroup;
    const isGroupParent = removeButton?.dataset.groupParent === 'true';

    if (groupId && isGroupParent) {
      return this.#removePersonalisationGroup(groupId, line);
    }

    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#getCartItemRow(line);

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal, cartTotalExclVat, itemCount } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
    });

    cartTotal?.shimmer();
    cartTotalExclVat?.shimmer();
    itemCount?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;
        
        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSectionElement(this, parsedResponseText.sections[this.sectionId]);
        queueMiniCartNostoRefresh();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
      });
  }

  #updatePersonalisationGroup(groupId, quantity) {
    const groupedRows = this.#getPersonalisationGroupRows(groupId);

    if (!groupedRows.length) return;

    const updates = Object.fromEntries(
      groupedRows
        .map((row) => [row.dataset.lineKey, quantity])
        .filter(([lineKey]) => typeof lineKey === 'string' && lineKey !== '')
    );

    if (!Object.keys(updates).length) return;

    this.#updateMultipleQuantities(updates);
  }

  #removePersonalisationGroup(groupId, line) {
    const groupedRows = this.#getPersonalisationGroupRows(groupId);
    const updates = Object.fromEntries(
      groupedRows
        .map((row) => [row.dataset.lineKey, 0])
        .filter(([lineKey]) => typeof lineKey === 'string' && lineKey !== '')
    );

    if (!Object.keys(updates).length) return;

    this.#updateMultipleQuantities(updates);

    const cartItemRowToRemove = this.#getCartItemRow(line);

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    onAnimationEnd(cartItemRowToRemove, remove);
  }

  #updateMultipleQuantities(updates) {
    this.#disableCartItems();

    const { cartTotal, cartTotalExclVat, itemCount } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      updates,
      sections: Array.from(sectionsToUpdate).join(','),
    });

    cartTotal?.shimmer();
    cartTotalExclVat?.shimmer();
    itemCount?.shimmer();

    fetch(Theme.routes.cart_update_url, fetchConfig('json', { body }))
      .then((response) => response.text())
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSectionElement(this, parsedResponseText.sections[this.sectionId]);
        queueMiniCartNostoRefresh();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const lineItemRow = this.#getCartItemRow(line);
    const quantityInput = lineItemRow?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSectionElement(this, this.sectionId, { cache: false }).then(() => {
        queueMiniCartNostoRefresh();
      });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSectionElement(this, cartItemsHtml);
      queueMiniCartNostoRefresh();
    } else {
      sectionRenderer.renderSectionElement(this, this.sectionId, { cache: false }).then(() => {
        queueMiniCartNostoRefresh();
      });
    }
  };

  /**
   * Handles cart drawer opening to refresh contents
   */
  #handleCartDrawerOpened = () => {
    // Try to refresh the cart contents when the drawer opens
    try {
      sectionRenderer.renderSectionElement(this, this.sectionId, { cache: false }).then(() => {
        queueMiniCartNostoRefresh();
      });
    } catch (error) {
      console.error('Error refreshing cart contents:', error);
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  #getCartItemRow(line) {
    return this.querySelector(`[ref="cartItemRows[]"][data-cart-line="${line}"]`);
  }

  #getPersonalisationGroupRows(groupId) {
    return Array.from(this.querySelectorAll('[ref="cartItemRows[]"][data-personalisation-group]')).filter(
      (row) => row instanceof HTMLElement && row.dataset.personalisationGroup === groupId
    );
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
